<template>
    <!-- Configuration d'un plat ou d'un menu — suppléments, options, quantité — rendue À PLAT
         sous la ligne du produit. Contenu, calculs de prix et validation sont ceux de la popup
         d'origine : seule l'enveloppe change (FoodDetailModal réutilise ce même panneau dans
         une CustomDialog). -->
    <div class="food-detail-panel" :class="{ 'is-inline': inline }">
        <div class="food-detail-v3">
            <button class="food-detail-close" @click="close">
                <X :size="22" />
            </button>

            <div v-if="loading" class="food-detail-loading">
                <div class="spinner-v3"></div>
                <p class="poppinsRegular ppv2-text-sm ppv2-text-black ppv2-opacity-60">
                    {{ $t('devis.v3.food_loading') }}
                </p>
            </div>

            <div v-else-if="!detail" class="food-detail-loading">
                <p class="poppinsRegular ppv2-text-sm ppv2-text-black ppv2-opacity-60">
                    {{ $t('devis.v3.food_detail_error') }}
                </p>
            </div>

            <div v-else class="food-detail-body">
                <!-- Colonne gauche : visuel, en forme POPUP uniquement. Déplié sous la ligne, le
                     plat a déjà sa vignette à gauche de cette même ligne : la répéter en grand ne
                     montrerait rien de neuf et prendrait la place des options.
                     `@error` couvre le cas d'une URL renseignée mais introuvable — sans lui la
                     carte affichait une image cassée. -->
                <div v-if="!inline" class="food-detail-media">
                    <img :src="imageSrc" :alt="detail.name" @error="imageFailed = true" />
                </div>

                <!-- Colonne droite : titre puis groupes d'addons / options -->
                <div class="food-detail-main custom-scrollbar">
                    <h3 class="poppinsBold ppv2-text-xl ppv2-text-black ppv2-mb-1">{{ detail.name }}</h3>
                    <p class="poppinsRegular ppv2-text-sm ppv2-text-black ppv2-opacity-60 ppv2-mb-1">
                        {{ formatPrice(basePrice) }}
                    </p>
                    <!-- La description est saisie en éditeur riche côté Paramètres : elle
                             arrive en HTML (`<p>…</p>`) et doit être rendue comme tel, sinon
                             les balises s'affichent en clair. Contenu produit par le personnel
                             de l'établissement, dans son propre back-office. -->
                    <div
                        v-if="description"
                        class="food-detail-description poppinsLight ppv2-text-sm ppv2-text-black ppv2-opacity-70 ppv2-mb-4"
                        v-html="description"
                    ></div>

                    <!-- Menu : chaque niveau de la formule attend un produit. La consigne
                             est donnée UNE fois en tête, plutôt que répétée sur chaque section. -->
                    <p v-if="isPack && groups.length > 0" class="food-pack-hint poppinsRegular ppv2-text-sm">
                        {{ $t('devis.v3.food_pack_choose_hint') }}
                    </p>

                    <div v-for="group in groups" :key="group.key" class="addon-group">
                        <div class="addon-group-head">
                            <span class="poppinsSemiBold ppv2-text-base ppv2-text-black">{{ group.name }}</span>
                            <span v-if="group.required" class="addon-badge-required poppinsMedium">
                                {{ $t('devis.v3.food_required') }}
                            </span>
                            <span
                                v-if="group.hint"
                                class="poppinsLight ppv2-text-xs ppv2-text-black ppv2-opacity-50 ppv2-ml-auto"
                            >
                                {{ group.hint }}
                            </span>
                        </div>

                        <template v-for="item in group.items">
                            <!-- ─── Produit d'un NIVEAU de menu : ligne à cocher ─────────
                                     Un niveau = un produit. Cocher bascule la sélection, et la
                                     configuration du produit retenu s'ouvre juste en dessous. -->
                            <div
                                v-if="group.kind === 'pack'"
                                :key="group.key + '-' + item.id"
                                class="addon-row addon-row--pack"
                                :class="{ 'is-picked': isPicked(group, item) }"
                                @click="pickPackItem(group, item)"
                            >
                                <span class="pack-radio" :class="{ checked: isPicked(group, item) }">
                                    <Check v-if="isPicked(group, item)" :size="12" />
                                </span>

                                <div class="addon-row-label">
                                    <span class="poppinsRegular ppv2-text-sm ppv2-text-black">{{ item.name }}</span>
                                    <span class="poppinsLight ppv2-text-xs ppv2-text-black ppv2-opacity-50">
                                        {{ $t('devis.v3.food_included') }}
                                    </span>
                                </div>

                                <!-- Le compteur ne s'affiche que sur le produit retenu : les
                                         autres sont à zéro par construction. -->
                                <div v-if="isPicked(group, item)" class="qty-stepper" @click.stop>
                                    <button type="button" :disabled="itemQty(group, item) <= 1" @click="decrement(group, item)">
                                        <Minus :size="14" />
                                    </button>
                                    <span class="qty-value poppinsSemiBold">{{ itemQty(group, item) }}</span>
                                    <button type="button" @click="increment(group, item)">
                                        <Plus :size="14" />
                                    </button>
                                </div>
                            </div>

                            <!-- Configuration du produit retenu, EN LIGNE et indentée sous
                                     lui. Rien n'est rendu si le produit n'a ni supplément ni
                                     option : un bloc vide ne dirait rien. -->
                            <div
                                v-if="group.kind === 'pack' && isPicked(group, item) && subGroupsOf(group, item).length > 0"
                                :key="group.key + '-' + item.id + '-config'"
                                class="pack-subconfig"
                            >
                                <div v-for="sub in subGroupsOf(group, item)" :key="sub.key" class="addon-group !ppv2-mt-3">
                                    <div class="addon-group-head">
                                        <span class="poppinsSemiBold ppv2-text-sm ppv2-text-black">{{ sub.name }}</span>
                                        <span v-if="sub.required" class="addon-badge-required poppinsMedium">
                                            {{ $t('devis.v3.food_required') }}
                                        </span>
                                        <span
                                            v-if="sub.hint"
                                            class="poppinsLight ppv2-text-xs ppv2-text-black ppv2-opacity-50 ppv2-ml-auto"
                                        >
                                            {{ sub.hint }}
                                        </span>
                                    </div>

                                    <div
                                        v-for="subItem in sub.items"
                                        :key="sub.key + '-' + subItem.id"
                                        class="addon-row"
                                        :class="{
                                            'addon-row--radio': isRadioGroup(sub),
                                            'is-picked': isRadioGroup(sub) && itemQty(sub, subItem) > 0,
                                        }"
                                        @click="isRadioGroup(sub) ? pickExclusive(sub, subItem) : null"
                                    >
                                        <!-- Une option est un choix EXCLUSIF, en quantité 1 :
                                                 pastille ronde, pas de compteur. -->
                                        <span
                                            v-if="isRadioGroup(sub)"
                                            class="pack-radio"
                                            :class="{ checked: itemQty(sub, subItem) > 0 }"
                                        >
                                            <Check v-if="itemQty(sub, subItem) > 0" :size="12" />
                                        </span>

                                        <div class="addon-row-label">
                                            <span class="ppv2-flex ppv2-items-center ppv2-gap-2">
                                                <span class="poppinsRegular ppv2-text-sm ppv2-text-black">
                                                    {{ subItem.name }}
                                                </span>
                                                <span v-if="subItem.required" class="addon-badge-required poppinsMedium">
                                                    {{ $t('devis.v3.food_required') }}
                                                </span>
                                            </span>
                                            <span class="poppinsLight ppv2-text-xs ppv2-text-black ppv2-opacity-50">
                                                {{
                                                    subItem.price > 0
                                                        ? '+ ' + formatPrice(subItem.price)
                                                        : $t('devis.v3.food_included')
                                                }}
                                            </span>
                                        </div>

                                        <div v-if="!isRadioGroup(sub)" class="qty-stepper">
                                            <button
                                                type="button"
                                                :disabled="itemQty(sub, subItem) === 0"
                                                @click="decrement(sub, subItem)"
                                            >
                                                <Minus :size="14" />
                                            </button>
                                            <span class="qty-value poppinsSemiBold">{{ itemQty(sub, subItem) }}</span>
                                            <button
                                                type="button"
                                                :disabled="!canIncrement(sub, subItem)"
                                                @click="increment(sub, subItem)"
                                            >
                                                <Plus :size="14" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- ─── Supplément / option d'un plat simple ───────────────── -->
                            <div
                                v-if="group.kind !== 'pack'"
                                :key="group.key + '-' + item.id"
                                class="addon-row"
                                :class="{
                                    'addon-row--radio': isRadioGroup(group),
                                    'is-picked': isRadioGroup(group) && itemQty(group, item) > 0,
                                }"
                                @click="isRadioGroup(group) ? pickExclusive(group, item) : null"
                            >
                                <!-- Une option est un choix EXCLUSIF, en quantité 1 :
                                         pastille ronde, pas de compteur. -->
                                <span
                                    v-if="isRadioGroup(group)"
                                    class="pack-radio"
                                    :class="{ checked: itemQty(group, item) > 0 }"
                                >
                                    <Check v-if="itemQty(group, item) > 0" :size="12" />
                                </span>

                                <div class="addon-row-label">
                                    <span class="ppv2-flex ppv2-items-center ppv2-gap-2">
                                        <span class="poppinsRegular ppv2-text-sm ppv2-text-black">{{ item.name }}</span>
                                        <!-- Un item peut être requis indépendamment de son groupe
                                                 (`is_required` sur food_category_addon_items). -->
                                        <span v-if="item.required" class="addon-badge-required poppinsMedium">
                                            {{ $t('devis.v3.food_required') }}
                                        </span>
                                    </span>
                                    <span class="poppinsLight ppv2-text-xs ppv2-text-black ppv2-opacity-50">
                                        {{ item.price > 0 ? '+ ' + formatPrice(item.price) : $t('devis.v3.food_included') }}
                                    </span>
                                </div>

                                <div v-if="!isRadioGroup(group)" class="qty-stepper">
                                    <button type="button" :disabled="itemQty(group, item) === 0" @click="decrement(group, item)">
                                        <Minus :size="14" />
                                    </button>
                                    <span class="qty-value poppinsSemiBold">{{ itemQty(group, item) }}</span>
                                    <button type="button" :disabled="!canIncrement(group, item)" @click="increment(group, item)">
                                        <Plus :size="14" />
                                    </button>
                                </div>
                            </div>
                        </template>
                    </div>

                    <p v-if="groups.length === 0" class="poppinsLight ppv2-text-sm ppv2-text-black ppv2-opacity-60">
                        {{ $t('devis.v3.food_no_addon') }}
                    </p>
                </div>
            </div>
        </div>

        <!-- Pied réservé à la forme POPUP. Déplié sous la ligne, le panneau n'a ni bouton de
             validation ni compteur : la configuration s'applique à chaque clic (`emitChange`)
             et la quantité se règle sur la ligne du produit, comme pour tout autre article. -->
        <div v-if="!inline" class="food-detail-panel-actions">
            <div class="qty-stepper qty-stepper--lg">
                <button type="button" :disabled="quantity <= 1" @click="quantity--">
                    <Minus :size="16" />
                </button>
                <span class="qty-value poppinsSemiBold">{{ quantity }}</span>
                <button type="button" @click="quantity++">
                    <Plus :size="16" />
                </button>
            </div>

            <BaseButton
                customClass="!ppv2-rounded-xl !ppv2-px-8 !poppinsBold"
                :disabled="!canConfirm"
                :title="missingRequired.length ? missingRequiredLabel : null"
                @click="confirm"
            >
                {{ $t('devis.v3.food_confirm') }}{{ formatPrice(total) }}
            </BaseButton>
        </div>
    </div>
</template>

<script>
import axios from 'axios'
import BaseButton from '../../../Shared/BaseButton.vue'
import { X, Plus, Minus, Check } from 'lucide-vue'

const PLACEHOLDER_IMAGE = '/media/placeholder_devis_items.jpeg'

export default {
    name: 'FoodDetailPanel',
    components: { BaseButton, X, Plus, Minus, Check },
    props: {
        // Rendu à plat sous la ligne du plat plutôt que dans une popup. Le drapeau ne pilote
        // que l'habillage : la logique de configuration est rigoureusement la même.
        inline: { type: Boolean, default: false },
        // Plat OU menu cliqué : sert d'amorce (uuid, `type`) et de repli pour le nom/prix
        // tant que le détail complet n'est pas arrivé.
        food: { type: Object, default: null },
        // Configuration déjà choisie pour ce plat, à réappliquer à la réouverture. On repasse
        // les deux objets BRUTS (`selections`, `packChoice`) plutôt que la liste aplatie des
        // options : ils se réinjectent tels quels, sans reconstruction, et rien ne se perd en
        // route — notamment la configuration propre à chaque produit d'un menu.
        initialState: { type: Object, default: null },
    },
    data() {
        return {
            detail: null,
            loading: false,
            imageFailed: false,
            quantity: 1,
            // Quantité choisie par item, indexée `<clé de groupe>::<id item>`. Un objet plat
            // plutôt qu'imbriqué : la réactivité Vue 2 n'aime pas les clés ajoutées en
            // profondeur, et la configuration en ligne d'un menu s'y range sans structure
            // supplémentaire — sa clé de groupe est simplement préfixée par celle du produit.
            selections: {},
            // Menu : produit retenu par niveau, `<clé de groupe> → <clé de l'item>`.
            packChoice: {},
        }
    },
    computed: {
        // Un menu (pack) se charge et se compose autrement : ses « groupes » sont les
        // niveaux de la formule, pas des suppléments.
        isPack() {
            return this.food?.type === 'pack'
        },
        basePrice() {
            return Number(this.detail?.price ?? this.food?.price ?? 0) || 0
        },
        description() {
            return this.detail?.short_description || this.detail?.description || ''
        },
        imageSrc() {
            if (this.imageFailed || !this.detail?.image) return PLACEHOLDER_IMAGE
            return this.detail.image
        },
        /**
         * Groupes de premier niveau.
         *
         * Menu  → une section par niveau de la formule ; les produits sont COMPRIS dans le
         *         prix (leur tarif catalogue ne s'additionne pas, sinon la formule serait
         *         facturée deux fois), mais leurs suppléments, eux, se facturent.
         * Plat  → ses options (attributs de carte) puis ses suppléments.
         */
        groups() {
            if (!this.isPack) {
                return this.buildGroups(this.detail, '')
            }

            return (this.detail?.items || []).map((section, index) => ({
                key: 'pack-' + index,
                kind: 'pack',
                // Un niveau de formule n'a pas d'identifiant propre en base : il n'existe
                // que par le nom porté par le pivot du menu. C'est ce nom que le serveur
                // attend dans `items[].name`.
                group_id: null,
                name: section.name,
                // Un niveau de formule attend un produit : il est requis par nature. Le pivot
                // du pack ne porte aucune borne, on impose donc le minimum sans plafonner.
                required: true,
                min: 1,
                max: 0,
                hint: '',
                items: (section.foods || []).map((food) => ({
                    id: food.id ?? food.uuid,
                    // L'uuid est l'identifiant que le serveur exige pour retrouver le plat
                    // (`foods.uuid`). `id` reste la clé de sélection interne à la popup.
                    uuid: food.uuid || null,
                    name: food.name,
                    price: 0,
                    maxQty: 0,
                    required: false,
                    source: food,
                })),
            }))
        },
        /**
         * Configurations en ligne actuellement ouvertes : celles du produit retenu de chaque
         * niveau, et seulement si ce produit a des suppléments ou des options.
         *
         * Indexées par clé de sélection du produit, pour que le rendu les retrouve sans
         * reconstruire quoi que ce soit — et pour qu'elles conservent leur identité tant que
         * la sélection ne bouge pas.
         */
        openSubGroups() {
            const open = {}

            if (!this.isPack) return open

            this.groups.forEach((group) => {
                group.items.forEach((item) => {
                    const key = this.selectionKey(group, item)
                    if (this.packChoice[group.key] !== key) return

                    const subGroups = this.buildGroups(item.source, key + '::')
                    if (subGroups.length > 0) open[key] = subGroups
                })
            })

            return open
        },
        // Tous les groupes qui comptent pour le prix et la validation : ceux de premier
        // niveau plus les configurations ouvertes. Les configurations d'un produit NON retenu
        // en sont absentes — elles ne doivent ni se facturer ni bloquer la validation.
        pricedGroups() {
            return this.groups.concat(...Object.values(this.openSubGroups))
        },
        // Prix d'UN plat (ou menu) configuré : base + tout ce qui a été coché.
        unitPrice() {
            let price = this.basePrice

            this.pricedGroups.forEach((group) => {
                group.items.forEach((item) => {
                    price += item.price * this.itemQty(group, item)
                })
            })

            return price
        },
        total() {
            return this.unitPrice * this.quantity
        },
        // Ce qu'il reste à choisir. Tant que la liste n'est pas vide, la validation est
        // bloquée — sinon on enverrait une ligne que la cuisine ne peut pas préparer
        // (« choisissez une boisson » ignoré, niveau de formule laissé vide).
        missingRequired() {
            const missing = []

            this.pricedGroups.forEach((group) => {
                if (group.required && this.groupQty(group) < Math.max(group.min, 1)) {
                    missing.push(group.name)
                    return
                }

                // Un item requis se contrôle à part : son groupe peut être satisfait par
                // d'autres choix alors que cet item précis reste obligatoire.
                group.items.forEach((item) => {
                    if (item.required && this.itemQty(group, item) === 0) {
                        missing.push(group.name + ' · ' + item.name)
                    }
                })
            })

            return missing
        },
        missingRequiredLabel() {
            return this.$t('devis.v3.food_required_missing', { groups: this.missingRequired.join(', ') })
        },
        canConfirm() {
            return !this.loading && !!this.detail && this.missingRequired.length === 0
        },
    },
    watch: {
        // Le panneau est monté au dépliage et démonté à la fermeture : le chargement suit donc
        // le plat, et repart de la configuration enregistrée s'il y en a une.
        food: {
            immediate: true,
            handler() {
                this.load()
            },
        },
        // Surveillance PROFONDE plutôt qu'un appel dans chaque méthode de sélection : toute
        // mutation de la configuration remonte, sans avoir à retoucher `setQty`, `pickPackItem`
        // ni aucun des chemins existants.
        selections: {
            deep: true,
            handler() {
                this.emitChange()
            },
        },
        packChoice: {
            deep: true,
            handler() {
                this.emitChange()
            },
        },
    },
    methods: {
        async load() {
            const uuid = this.food?.uuid
            this.detail = null
            this.quantity = 1
            // Réouverture : on repart de la configuration enregistrée plutôt que d'une page
            // blanche — « je le clique, je le retrouve déjà sélectionné ».
            this.selections = { ...(this.initialState?.selections || {}) }
            this.packChoice = { ...(this.initialState?.packChoice || {}) }
            this.imageFailed = false

            if (!uuid) return

            const url = this.isPack ? `/api/v3/food/pack/show/${uuid}` : `/api/v3/food/show/${uuid}`

            this.loading = true
            try {
                const response = await axios.get(url)
                this.detail = response.data?.data || null
            } catch (error) {
                console.error('Chargement du détail impossible :', error)
                this.detail = null
            } finally {
                this.loading = false
            }

            // Première remontée : sans elle, un plat dont AUCUN groupe n'est obligatoire (donc
            // déjà complet à l'ouverture) n'entrerait au panier qu'au premier clic sur une
            // option — alors que le déplier suffit à le choisir.
            this.emitChange()
        },
        /**
         * Normalise options et suppléments d'un plat en une seule forme de groupe.
         *
         * Les deux viennent de sources différentes — `options` porte des `values`, `addons`
         * des `items` — mais s'affichent, se totalisent et se contrôlent de la même façon.
         * Le préfixe rend les clés uniques quand ces groupes sont imbriqués sous un produit
         * de menu : deux produits peuvent partager le même groupe de suppléments.
         */
        buildGroups(source, prefix) {
            const groups = []

            ;(source?.options || []).forEach((option) => {
                groups.push({
                    key: prefix + 'opt-' + option.id,
                    kind: 'option',
                    // Identifiant de l'ATTRIBUT de carte. Le serveur retrouve la valeur
                    // choisie par le couple (attributeId, valueId) — la clé d'affichage,
                    // préfixée pour rester unique dans un menu, ne lui parle pas.
                    group_id: option.id,
                    name: option.name,
                    required: false,
                    min: 0,
                    max: 0,
                    hint: '',
                    items: (option.values || []).map((value) => ({
                        id: value.id,
                        name: value.name,
                        price: Number(value.price) || 0,
                        maxQty: 0,
                        required: false,
                    })),
                })
            })
            ;(source?.addons || []).forEach((addon) => {
                const min = Number(addon.min_quantity) || 0
                const max = Number(addon.max_quantity) || 0

                groups.push({
                    key: prefix + 'add-' + addon.id,
                    kind: 'addon',
                    // Identifiant du GROUPE de suppléments. Le serveur, lui, n'a besoin que
                    // de l'item retenu (itemId) — on le remonte pour la traçabilité.
                    group_id: addon.id,
                    name: addon.name,
                    required: !!addon.is_required || min > 0,
                    min,
                    max,
                    hint: this.groupHint(min, max),
                    items: (addon.items || []).map((item) => ({
                        id: item.id,
                        name: item.name,
                        // Un item « offert » ne coûte rien même s'il porte un prix catalogue.
                        price: item.is_free ? 0 : Number(item.price) || 0,
                        // `force_one_qty` = un exemplaire au maximum pour cet item.
                        maxQty: item.force_one_qty ? 1 : 0,
                        // Exigence propre à l'item, indépendante du minimum du groupe.
                        required: !!item.is_required,
                    })),
                })
            })

            return groups
        },
        subGroupsOf(group, item) {
            return this.openSubGroups[this.selectionKey(group, item)] || []
        },
        isPicked(group, item) {
            return this.packChoice[group.key] === this.selectionKey(group, item)
        },
        /**
         * Une OPTION (attribut de carte : sauce, cuisson, taille) est un choix exclusif en
         * quantité 1 — on ne prend pas « deux fois la sauce César ». Elle se coche donc,
         * sans compteur. Les SUPPLÉMENTS gardent leur compteur : eux se cumulent réellement,
         * et la base leur donne des bornes (min_quantity / max_quantity).
         */
        isRadioGroup(group) {
            return group.kind === 'option'
        },
        /**
         * Coche une option et décoche les autres du même groupe.
         *
         * Recliquer sur l'option retenue la retire : c'est le seul moyen de revenir à « aucun
         * choix », et rien n'oblige à en prendre une — l'API n'expose aucun caractère
         * obligatoire sur les attributs de carte.
         */
        pickExclusive(group, item) {
            const key = this.selectionKey(group, item)
            const wasPicked = this.itemQty(group, item) > 0

            group.items.forEach((other) => {
                this.$set(this.selections, this.selectionKey(group, other), 0)
            })

            if (!wasPicked) {
                this.$set(this.selections, key, 1)
            }
        },
        /**
         * Coche un produit du niveau. Un niveau = un produit : le précédent repasse à zéro.
         *
         * Ses réglages ne sont PAS effacés — revenir dessus les retrouve intacts. Ils ne
         * comptent simplement plus, ni dans le prix ni dans la validation, tant qu'il n'est
         * pas le produit retenu (`pricedGroups` ne voit que la configuration ouverte).
         */
        pickPackItem(group, item) {
            const key = this.selectionKey(group, item)

            if (this.packChoice[group.key] === key) return

            group.items.forEach((other) => {
                this.$set(this.selections, this.selectionKey(group, other), 0)
            })

            this.$set(this.packChoice, group.key, key)
            this.$set(this.selections, key, 1)
        },
        groupHint(min, max) {
            if (min > 0 && max > 0) return this.$t('devis.v3.food_min_max', { min, max })
            if (min > 0) return this.$t('devis.v3.food_min', { min })
            if (max > 0) return this.$t('devis.v3.food_max', { max })
            return ''
        },
        selectionKey(group, item) {
            return group.key + '::' + item.id
        },
        itemQty(group, item) {
            return this.selections[this.selectionKey(group, item)] || 0
        },
        groupQty(group) {
            return group.items.reduce((sum, item) => sum + this.itemQty(group, item), 0)
        },
        // `0` = pas de plafond, côté item comme côté groupe (convention de la base).
        canIncrement(group, item) {
            if (item.maxQty > 0 && this.itemQty(group, item) >= item.maxQty) return false
            if (group.max > 0 && this.groupQty(group) >= group.max) return false
            return true
        },
        increment(group, item) {
            if (!this.canIncrement(group, item)) return
            this.$set(this.selections, this.selectionKey(group, item), this.itemQty(group, item) + 1)
        },
        decrement(group, item) {
            const next = this.itemQty(group, item) - 1
            if (next < 0) return
            this.$set(this.selections, this.selectionKey(group, item), next)
        },
        formatPrice(value) {
            const amount = Number(value)
            return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(
                Number.isFinite(amount) ? amount : 0
            )
        },
        /**
         * Les choix à plat, avec leur libellé et leur prix : le panier n'a alors plus besoin du
         * détail complet pour s'afficher ni pour se totaliser. Un produit de menu emporte sa
         * propre configuration dans `extras`, pour que la composition parvienne entière.
         */
        buildOptions() {
            const options = []

            this.groups.forEach((group) => {
                group.items.forEach((item) => {
                    const quantity = this.itemQty(group, item)
                    if (quantity <= 0) return

                    options.push({
                        group_key: group.key,
                        group_id: group.group_id,
                        group_name: group.name,
                        kind: group.kind,
                        id: item.id,
                        uuid: item.uuid || null,
                        name: item.name,
                        price: item.price,
                        quantity,
                        extras: this.flattenSelections(this.subGroupsOf(group, item)),
                    })
                })
            })

            return options
        },
        /**
         * Remonte la configuration courante à chaque changement.
         *
         * `ready` dit si elle est complète : tant qu'un groupe obligatoire manque, l'appelant
         * ne doit rien mettre au panier — c'est ce que faisait le bouton désactivé.
         * `selections` et `packChoice` repartent bruts pour pouvoir revenir tels quels.
         */
        emitChange() {
            if (!this.detail) return

            const options = this.buildOptions()

            this.$emit('change', {
                food: this.detail,
                is_pack: this.isPack,
                options,
                count: options.length,
                unit_price: this.unitPrice,
                base_price: this.basePrice,
                ready: this.canConfirm,
                missing: this.missingRequired.length ? this.missingRequiredLabel : null,
                selections: { ...this.selections },
                packChoice: { ...this.packChoice },
            })
        },
        confirm() {
            if (!this.canConfirm) return

            const options = this.buildOptions()

            this.$emit('confirm', {
                food: this.detail,
                // `is_pack` voyage avec la sélection : `detail` provient de deux endpoints
                // différents et un menu ne se distingue d'un plat par aucun champ commun.
                is_pack: this.isPack,
                quantity: this.quantity,
                options,
                unit_price: this.unitPrice,
                total: this.total,
            })
            this.close()
        },
        flattenSelections(groups) {
            const selected = []

            groups.forEach((group) => {
                group.items.forEach((item) => {
                    const quantity = this.itemQty(group, item)
                    if (quantity > 0) {
                        selected.push({
                            group_key: group.key,
                            group_id: group.group_id,
                            group_name: group.name,
                            kind: group.kind,
                            id: item.id,
                            name: item.name,
                            price: item.price,
                            quantity,
                        })
                    }
                })
            })

            return selected
        },
        close() {
            this.$emit('close')
        },
    },
}
</script>

<style scoped>
/* Déplié, le panneau n'a pas la chrome d'une popup : pas de croix de fermeture, hauteur libre.
   Le reste de l'habillage est repris tel quel. */
.food-detail-panel.is-inline .food-detail-close {
    display: none;
}

/* Sans la colonne visuelle, le corps n'a plus qu'une colonne — et elle prend toute la largeur
   disponible sous la ligne, ce qui laisse respirer les groupes d'options. */
.food-detail-panel.is-inline .food-detail-body {
    grid-template-columns: minmax(0, 1fr);
}

.food-detail-panel-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 12px;
    padding-top: 14px;
    margin-top: 14px;
    border-top: 1px solid #e9edf3;
}
.food-detail-v3 {
    position: relative;
    min-height: 220px;
}

.food-detail-close {
    position: absolute;
    top: -6px;
    right: -6px;
    z-index: 3;
    color: #1d1d1b;
    opacity: 0.6;
    transition: opacity 0.2s ease;
}

.food-detail-close:hover {
    opacity: 1;
}

.food-detail-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    min-height: 220px;
}

.food-detail-body {
    display: grid;
    grid-template-columns: minmax(0, 38%) minmax(0, 1fr);
    gap: 24px;
}

.food-detail-media {
    border-radius: 16px;
    overflow: hidden;
    background: #f1f5f9;
    align-self: start;
    aspect-ratio: 1 / 1;
}

.food-detail-media img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.food-detail-main {
    max-height: 52vh;
    overflow-y: auto;
    padding-right: 8px;
}

/* Le contenu injecté par v-html n'est PAS atteint par les styles scopés (aucun attribut
   data-v- ne lui est posé) : il faut ::v-deep, sinon les marges des <p> restent celles du
   navigateur et la description colle aux groupes. */
.food-detail-description ::v-deep p {
    margin: 0 0 6px;
}

.food-detail-description ::v-deep p:last-child {
    margin-bottom: 0;
}

.food-detail-description ::v-deep ul,
.food-detail-description ::v-deep ol {
    margin: 0 0 6px;
    padding-left: 18px;
}

.food-pack-hint {
    margin: 0 0 4px;
    padding: 10px 12px;
    border-radius: 10px;
    color: #7d2dbd;
    background: rgba(125, 45, 189, 0.08);
}

.addon-group {
    margin-top: 18px;
}

.addon-group-head {
    display: flex;
    align-items: center;
    gap: 8px;
    padding-bottom: 8px;
    border-bottom: 1px solid rgba(29, 29, 27, 0.1);
}

.addon-badge-required {
    font-size: 11px;
    line-height: 1;
    padding: 4px 8px;
    border-radius: 999px;
    color: #7d2dbd;
    background: rgba(125, 45, 189, 0.12);
}

.addon-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 10px 0;
    border-bottom: 1px solid rgba(29, 29, 27, 0.06);
}

.addon-row--pack,
.addon-row--radio {
    cursor: pointer;
    border-radius: 8px;
    padding: 10px 8px;
    transition: background 0.2s ease;
}

.addon-row--pack:hover,
.addon-row--radio:hover {
    background: rgba(125, 45, 189, 0.06);
}

.addon-row--pack.is-picked,
.addon-row--radio.is-picked {
    background: rgba(125, 45, 189, 0.08);
}

.pack-radio {
    flex-shrink: 0;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    border: 1.5px solid rgba(29, 29, 27, 0.3);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    transition: all 0.2s ease;
}

.pack-radio.checked {
    background: #7d2dbd;
    border-color: #7d2dbd;
}

/* Configuration en ligne : indentée et rattachée visuellement au produit retenu par un
   filet, pour qu'on lise à qui elle appartient sans avoir à la deviner. */
.pack-subconfig {
    margin: 0 0 8px 26px;
    padding-left: 14px;
    border-left: 2px solid rgba(125, 45, 189, 0.25);
}

.addon-row-label {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
    flex: 1;
}

.qty-stepper {
    display: flex;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;
    border: 1px solid rgba(29, 29, 27, 0.15);
    border-radius: 999px;
    padding: 2px;
    background: #fff;
}

.qty-stepper button {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #7d2dbd;
    transition: background 0.2s ease;
}

.qty-stepper button:hover:not(:disabled) {
    background: rgba(125, 45, 189, 0.1);
}

.qty-stepper button:disabled {
    opacity: 0.3;
    cursor: not-allowed;
}

.qty-value {
    min-width: 22px;
    text-align: center;
    font-size: 13px;
    color: #1d1d1b;
}

.qty-stepper--lg button {
    width: 32px;
    height: 32px;
}

.qty-stepper--lg .qty-value {
    min-width: 28px;
    font-size: 15px;
}

.spinner-v3 {
    width: 28px;
    height: 28px;
    border: 3px solid rgba(125, 45, 189, 0.2);
    border-top-color: #7d2dbd;
    border-radius: 50%;
    animation: food-spin 0.8s linear infinite;
}

@keyframes food-spin {
    to {
        transform: rotate(360deg);
    }
}

@media (max-width: 768px) {
    .food-detail-body {
        grid-template-columns: minmax(0, 1fr);
    }
}
</style>
